import React, { useState, useMemo } from "react";
import { Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { ProductTimeSeriesData } from "./productTypes";
import { prepareProductComparisonData } from "./ProductsController";
import { formatCurrency } from "../FinancialDashboardController";

interface Props {
  selectedProductCodes: string[];
  timeSeries: ProductTimeSeriesData[];
}

const ProductComparisonChart: React.FC<Props> = ({
  selectedProductCodes,
  timeSeries,
}) => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<"units" | "revenue">("revenue");

  // Prepare chart data
  const chartData = useMemo(() => {
    if (!selectedProductCodes.length) {
      return { labels: [], datasets: [] };
    }

    return prepareProductComparisonData(
      timeSeries,
      selectedProductCodes,
      viewMode,
    );
  }, [timeSeries, selectedProductCodes, viewMode]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: "top" as const,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const label = context.dataset.label || "";
            const value = context.parsed.y;
            const formattedValue =
              viewMode === "revenue" ? formatCurrency(value) : value.toString();
            return `${label}: ${formattedValue}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value: string | number) => {
            const numValue =
              typeof value === "string" ? parseFloat(value) : value;
            return viewMode === "revenue"
              ? formatCurrency(numValue)
              : numValue.toString();
          },
        },
      },
    },
  };

  return (
    <div className="product-comparison-section">
      <h2>{t("productsdashboard.product-comparison")}</h2>
      <div className="filters-bar">

          {/* View mode */}
          <div className="filter-group">
            <label>{t("financialdashboard.period")}</label>
            <div className="period-buttons">
              <span
                className={`range-label ${viewMode === "units" ? "active" : ""}`}
                onClick={() => setViewMode("units")}
              >
                {t("productsdashboard.view-mode.units")}
              </span>
              <span
                className={`range-label ${viewMode === "revenue" ? "active" : ""}`}
                onClick={() => setViewMode("revenue")}
              >
                {t("productsdashboard.view-mode.revenue")}
              </span>
            </div>
          </div>
      </div>

      {selectedProductCodes.length > 0 ? (
        <div className="chart-wrapper">
          <Line data={chartData} options={chartOptions} />
        </div>
      ) : (
        <div className="no-selection">
          {t("productsdashboard.select-products")}
        </div>
      )}
    </div>
  );
};

export default ProductComparisonChart;
